import React, { useEffect, useCallback } from "react";
import { ErrorCircle24Regular, CheckmarkCircle24Regular } from "@fluentui/react-icons";
import "./ErrorNotification.css";

export type NotificationSeverity = "error" | "success";

interface ErrorNotificationProps {
    title: string;
    message: string;
    severity?: NotificationSeverity;
    onClose: () => void;
}

export const ErrorNotification: React.FC<ErrorNotificationProps> = ({ title, message, severity = "error", onClose }) => {
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        },
        [onClose]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "";
        };
    }, [handleKeyDown]);

    const isSuccess = severity === "success";

    return (
        <div
            className="error-notification-backdrop"
            onClick={onClose}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="error-notification-title"
            aria-describedby="error-notification-message"
        >
            <div
                className={`error-notification-dialog${isSuccess ? " error-notification-dialog--success" : ""}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="error-notification-header">
                    <div className="error-notification-header-left">
                        <div className={`error-notification-icon${isSuccess ? " error-notification-icon--success" : ""}`}>
                            {isSuccess ? <CheckmarkCircle24Regular /> : <ErrorCircle24Regular />}
                        </div>
                        <span id="error-notification-title" className="error-notification-title">
                            {title}
                        </span>
                    </div>
                </div>
                <div className="error-notification-body">
                    <p id="error-notification-message" className="error-notification-message">
                        {message}
                    </p>
                </div>
                <div className="error-notification-footer">
                    <button className="error-notification-btn-close" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};
